import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Typography } from '@mui/material';
import { InfoText, StepWrapper, StepperControls, StyledCardSection } from '@fnol/library/src/components';
import { GenericObject, common, getApplicationId, getIsSalesForce } from '@fnol/library/src/lib';
import { personType, riskTypeLife, translations } from '../../lib/consts';
import { useValidateIndividual, useValidateLegalEntity } from '../../lib/customHooks';
import UsersAddPerson from '../UsersAddPerson';
import Individual from '../Individual/Individual';
import AMLSection from '../AMLSection';
import LegalEntity from '../LegalEntity';

type PolicyUserProps = {
    navigator: GenericObject[];
};

interface FormData {
    inputPersonOpen: boolean;
    user1Name: string;
    user2Name: string;
    user3Name: string;
    user4Name: string;
    user5Name: string;
    userName: string;
    validUserData: boolean;
    risks: number[];
    isSubmitterUser: string;
    submitterName: string;
    usersList: GenericObject[];
    userTypePurchase: number;
}

const UsersDetailScreen = ({ navigator }: PolicyUserProps) => {
    const [currentStep, { goBack, goForward }] = navigator;
    const { values, setErrors, setTouched, setFieldValue } = useFormikContext<FormData>();
    const userValidationIndividual = useValidateIndividual(personType.user);
    const userValidationLegalEntity = useValidateLegalEntity(personType.user);

    useEffect(() => {
        if (values.user1Name && values.user1Name !== '') {
            setFieldValue('submitError', '');
        }
    }, [values.user1Name]);

    const userIds = Array.from(Array(values.usersList?.length).keys()).map((userId) => userId + 1);
    const validateIndividual = values.risks.includes(riskTypeLife.outlive)
        ? useValidateIndividual(personType.user, userIds)
        : values.risks.includes(riskTypeLife.purchase)
        ? useValidateIndividual(personType.user)
        : null;

    const handleNext = (): void => {
        let valid = true;
        setTouched(
            {
                userName: true,
                user1Name: true,
                user2Name: true,
                user3Name: true,
                user4Name: true,
                user5Name: true,
                ...userValidationIndividual.touched,
                ...userValidationLegalEntity.touched,
            },
            false,
        );

        let existingErrors: GenericObject = {};

        if (
            values.isSubmitterUser === 'false' &&
            !values.userName &&
            !values.user1Name &&
            !values.user2Name &&
            !values.user3Name &&
            !values.user4Name &&
            !values.user5Name
        ) {
            valid = false;
            setFieldValue('submitError', translations.validationMessages.ADD_USER);
        }

        if (
            values.risks.includes(riskTypeLife.purchase) &&
            values.userTypePurchase === 1 &&
            userValidationIndividual.isValid === false
        ) {
            valid = false;
        }

        if (
            values.risks.includes(riskTypeLife.purchase) &&
            values.userTypePurchase === 2 &&
            !userValidationLegalEntity.isValid
        ) {
            valid = false;
        }

        existingErrors = {
            ...existingErrors,
            ...userValidationIndividual.errors,
            ...userValidationLegalEntity.errors,
        };

        setTimeout(() => {
            setErrors(existingErrors);
        });

        if (valid) {
            goForward();
        }
    };

    const handleGoBack = (): void => {
        setFieldValue('submitError', '');
        goBack();
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                {!values.risks.includes(riskTypeLife.purchase) ? (
                    <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                        {values.risks.includes(riskTypeLife.outlive)
                            ? translations.policyUser.ADD_USER_DATA_OUTLIVE
                            : translations.policyUser.ADD_USER_DATA}
                    </Typography>
                ) : (
                    <InfoText
                        label={
                            <>
                                <span>{translations.policyUser.ADD_USER_DATA_PURCHASE_1}</span>
                                <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                    {translations.policyUser.ADD_USER_DATA_PURCHASE_2}
                                </span>
                                <span>{translations.policyUser.ADD_USER_DATA_PURCHASE_3}</span> <br />
                                {values.userTypePurchase === 2 && (
                                    <span style={{ fontWeight: 'bold' }}>
                                        {translations.policyUser.ADD_USER_DATA_PURCHASE_4}
                                    </span>
                                )}
                            </>
                        }
                    ></InfoText>
                )}

                <UsersAddPerson></UsersAddPerson>

                {values.isSubmitterUser === 'false' &&
                    !(
                        values.risks.includes(riskTypeLife.death) ||
                        values.risks.includes(riskTypeLife.deathByAccident) ||
                        values.risks.includes(riskTypeLife.deathByAccident)
                    ) &&
                    !values.risks.includes(riskTypeLife.outlive) && (
                        <>
                            <Individual type={personType.user}></Individual>
                            {getIsSalesForce(getApplicationId()) && <AMLSection type={personType.user}></AMLSection>}
                        </>
                    )}

                {values.risks.includes(riskTypeLife.purchase) && (
                    <>
                        {values.userTypePurchase === 1 && <Individual type={personType.user}></Individual>}
                        {values.userTypePurchase === 2 && <LegalEntity type={personType.user}></LegalEntity>}
                        {getIsSalesForce(getApplicationId()) && <AMLSection type={personType.user}></AMLSection>}
                    </>
                )}
            </StyledCardSection>

            <StepperControls
                onNext={
                    (!values.inputPersonOpen && !values.risks.includes(riskTypeLife.outlive)) ||
                    (!values.inputPersonOpen && !!validateIndividual.isValid)
                        ? handleNext
                        : undefined
                }
                onPrevious={!values.inputPersonOpen ? handleGoBack : undefined}
                onNextLabel={common.NEXT}
                onPreviousLabel={common.PREVIOUS}
            />
        </StepWrapper>
    );
};

export default UsersDetailScreen;
